import { useState, type CSSProperties } from 'react';

import {
  generateCodeVerifier,
  computeCodeChallenge,
  buildAuthorizeUrl,
  persistVerifier,
} from '@/lib/auth/oauthRedirect';
import { getSharedSpreadsheetId } from '@/lib/sharing';
import type { SilentRefreshFailureReason } from '@/lib/auth/silentRefreshErrors';

/**
 * Inline Google "G" mark — path data copied verbatim from
 * `design/project/shared.jsx` (the `I.google` icon).
 */
function GoogleIcon({ size = 18 }: { size?: number }): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M22 12.2c0-.7-.06-1.4-.18-2H12v3.8h5.6c-.24 1.3-.97 2.4-2.07 3.14v2.6h3.35c1.96-1.8 3.12-4.47 3.12-7.54z"
      />
      <path
        fill="#34A853"
        d="M12 22c2.8 0 5.16-.93 6.88-2.52l-3.35-2.6c-.93.62-2.12.99-3.53.99-2.72 0-5.02-1.83-5.84-4.3H2.7v2.68A10 10 0 0012 22z"
      />
      <path
        fill="#FBBC05"
        d="M6.16 13.57a6 6 0 010-3.83V7.06H2.7a10 10 0 000 9.19l3.46-2.68z"
      />
      <path
        fill="#EA4335"
        d="M12 5.88c1.53 0 2.9.53 3.98 1.56l2.98-2.98C17.15 2.84 14.79 2 12 2a10 10 0 00-9.3 5.06l3.46 2.68C7 7.71 9.3 5.88 12 5.88z"
      />
    </svg>
  );
}

export interface LoginScreenProps {
  /**
   * Reason the cold-load silent refresh failed (or `null` if there was no
   * failure / it has been cleared). Drives the actionable copy shown in
   * the inline error banner.
   * Distinct from interactive sign-in errors, which are tracked locally
   * by `useState<ErrorState>` below.
   */
  coldLoadFailureReason?: SilentRefreshFailureReason | null;
  /**
   * When true, shows a migration notice banner informing the user that the
   * login mechanism was upgraded and they need to sign in once more.
   * Detected by App.tsx before session.ts cleanup runs (see design.md Decision 8).
   */
  migrationNotice?: boolean;
}

interface ErrorState {
  /** Localized message displayed inline. */
  message: string;
  /** Error code for debugging / telemetry. */
  code?: string;
}

/**
 * Map a cold-load failure reason to user-facing copy.
 * Covers both the original GIS reasons and the new server-backed reasons.
 * See spec.md "Silent Token Refresh on Expiry" + design.md Decision 8.
 */
const coldLoadMessageFor = (reason: SilentRefreshFailureReason): string => {
  switch (reason) {
    case 'no_session':
      return '請重新登入。';
    case 'invalid_session':
      return '登入已過期，請重新登入。';
    case 'grant_revoked':
      return '登入授權已過期，請重新登入。';
    case 'app_check_failed':
      return '瀏覽器封鎖了背景授權，請重新登入。';
    case 'upstream_unavailable':
      return '暫時無法刷新登入，請稍後再試。';
    case 'idle_7d_exceeded':
      return '已超過 7 天未使用，請重新登入。';
    case 'browser_blocked':
      return '瀏覽器封鎖了背景授權，請重新登入。\niPhone Safari：設定 → Safari → 防止跨網站追蹤 關閉。';
    case 'unknown':
    default:
      return '登入失敗，請再試一次。';
  }
};

export default function LoginScreen({
  coldLoadFailureReason = null,
  migrationNotice = false,
}: LoginScreenProps): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ErrorState | null>(null);

  // Cold-load failure (passed in from App.tsx) takes precedence over the
  // interactive error state when present — it's the more recent signal and
  // the user hasn't clicked anything yet on this mount.
  const displayedError: ErrorState | null = error
    ? error
    : coldLoadFailureReason !== null
      ? { message: coldLoadMessageFor(coldLoadFailureReason), code: coldLoadFailureReason }
      : null;

  const handleGoogleLogin = async (): Promise<void> => {
    if (busy) return;
    setBusy(true);
    try {
      const verifier = generateCodeVerifier();
      const challenge = await computeCodeChallenge(verifier);
      persistVerifier(verifier);
      const url = buildAuthorizeUrl({
        clientId: import.meta.env.VITE_GOOGLE_CLIENT_ID ?? '',
        redirectUri: `${window.location.origin}/auth/callback`,
        codeChallenge: challenge,
      });
      // Navigate to Google's authorization endpoint. This never returns —
      // the page redirects to /auth/callback after consent.
      window.location.assign(url);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[Mochia auth] failed to initiate OAuth redirect', err);
      setError({ message: '登入失敗，請再試一次。', code: 'redirect_failed' });
      setBusy(false);
    }
  };

  return (
    <main
      aria-labelledby="login-title"
      style={{
        minHeight: '100dvh',
        background: 'var(--bg-app)',
        display: 'flex',
        flexDirection: 'column',
        padding: '120px 28px 40px',
        position: 'relative',
        boxSizing: 'border-box',
      }}
    >
      {/* Logo + brand */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 14,
          marginBottom: 56,
        }}
      >
        <div
          aria-hidden="true"
          style={{
            width: 72,
            height: 72,
            borderRadius: 22,
            background: 'var(--espresso-900)',
            display: 'grid',
            placeItems: 'center',
            position: 'relative',
            boxShadow: 'var(--shadow-2)',
          }}
        >
          <div
            style={{
              width: 44,
              height: 40,
              borderRadius: '50% 50% 46% 46% / 60% 60% 40% 40%',
              background: 'var(--cream-50)',
              position: 'relative',
            }}
          >
            <div
              style={{
                position: 'absolute',
                top: 8,
                right: 7,
                width: 10,
                height: 10,
                borderRadius: '50%',
                background: 'var(--espresso-900)',
              }}
            />
          </div>
        </div>
        <h1
          id="login-title"
          style={{
            font: '700 28px var(--font-cjk-display)',
            color: 'var(--fg-1)',
            letterSpacing: '-0.01em',
            margin: 0,
          }}
        >
          Mochia · 麻吉記帳
        </h1>
        <div style={{ font: '400 14px var(--font-cjk)', color: 'var(--fg-3)' }}>
          家庭共享・離線可記
        </div>
      </div>

      {/* Sign-in section */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {/* Migration notice banner — shown when upgrading from the old GIS Token Model */}
        {migrationNotice && (
          <div
            role="status"
            data-testid="migration-notice"
            style={{
              padding: '12px 14px',
              borderRadius: 12,
              background: 'rgba(199,154,75,0.12)',
              border: '1px solid rgba(199,154,75,0.24)',
              font: '400 13px/1.6 var(--font-cjk)',
              color: 'var(--fg-1)',
            }}
          >
            已升級登入機制，請重新登入一次以延長未來登入有效期。
          </div>
        )}

        {(() => {
          const joined = getSharedSpreadsheetId();
          if (joined) {
            return (
              <p
                style={{
                  margin: 0,
                  font: '500 14px/1.6 var(--font-cjk)',
                  color: 'var(--fg-1)',
                  textAlign: 'center',
                  padding: '12px 14px',
                  background: 'rgba(199,154,75,0.12)',
                  borderRadius: 12,
                  border: '1px solid rgba(199,154,75,0.24)',
                }}
                data-testid="join-hint"
              >
                你被邀請加入家庭帳本。
                <br />
                登入後會直接打開共享的試算表。
              </p>
            );
          }
          return (
            <p
              style={{
                margin: 0,
                font: '400 14px/1.6 var(--font-cjk)',
                color: 'var(--fg-2)',
                textAlign: 'center',
              }}
            >
              以你的 Google 帳號登入。
              <br />
              資料會存進你自己的 Google 試算表。
            </p>
          );
        })()}

        <button
          type="button"
          onClick={() => void handleGoogleLogin()}
          disabled={busy}
          aria-busy={busy}
          style={primaryGoogleButtonStyle(busy)}
        >
          <GoogleIcon size={18} />
          {busy ? '登入中…' : '以 Google 登入'}
        </button>

        {displayedError && (
          <div
            role="alert"
            style={{
              marginTop: 4,
              padding: '12px 14px',
              borderRadius: 12,
              background: 'rgba(167, 75, 58, 0.08)',
              border: '1px solid rgba(167, 75, 58, 0.24)',
              color: 'var(--expense)',
              font: '400 13px/1.6 var(--font-cjk)',
              whiteSpace: 'pre-line',
            }}
            data-testid="login-error"
          >
            {displayedError.message}
          </div>
        )}

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            margin: '14px 0',
          }}
        >
          <div style={{ flex: 1, height: 0.5, background: 'var(--border-1)' }} />
          <span style={{ font: '400 12px var(--font-cjk)', color: 'var(--fg-3)' }}>
            僅支援 Google 登入
          </span>
          <div style={{ flex: 1, height: 0.5, background: 'var(--border-1)' }} />
        </div>

        <p
          style={{
            margin: 0,
            font: '400 12px/1.7 var(--font-cjk)',
            color: 'var(--fg-3)',
            textAlign: 'center',
          }}
        >
          需要授權 Google Sheets 存取權限。
          <br />
          Mochia 不會儲存你的密碼，也不會把資料傳給第三方。
        </p>
      </div>

      <div
        style={{
          marginTop: 'auto',
          display: 'flex',
          justifyContent: 'center',
          gap: 20,
          font: '400 13px var(--font-cjk)',
          color: 'var(--fg-3)',
        }}
      >
        <span>v1 · 麻吉記帳</span>
      </div>
    </main>
  );
}

function primaryGoogleButtonStyle(busy: boolean): CSSProperties {
  return {
    marginTop: 10,
    padding: '15px',
    background: 'var(--accent)',
    color: '#fff',
    border: 0,
    borderRadius: 999,
    font: '500 16px var(--font-cjk)',
    cursor: busy ? 'not-allowed' : 'pointer',
    opacity: busy ? 0.7 : 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    transition: 'opacity 120ms ease',
  };
}
